import { CareerLevel, ConsultantNode } from '../types/career';

/**
 * Finds a node in the tree by its ID.
 */
export function findNodeById(root: ConsultantNode, id: string): ConsultantNode | null {
  if (root.id === id) {
    return root;
  }
  for (const child of root.children) {
    const found = findNodeById(child, id);
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Finds the path of node IDs from root to target node ID.
 * Returns array of IDs: [root.id, ..., targetId].
 */
export function findPathToNode(root: ConsultantNode, targetId: string): string[] {
  function search(current: ConsultantNode, path: string[]): string[] | null {
    const currentPath = [...path, current.id];
    if (current.id === targetId) {
      return currentPath;
    }
    for (const child of current.children) {
      const result = search(child, currentPath);
      if (result) {
        return result;
      }
    }
    return null;
  }

  return search(root, []) || [];
}

/**
 * Counts total consultants in the subtree (including root).
 */
export function countTotalConsultants(root: ConsultantNode): number {
  let count = 1;
  for (const child of root.children) {
    count += countTotalConsultants(child);
  }
  return count;
}

/**
 * Counts active consultants in the subtree (including root if active).
 */
export function countActiveConsultants(root: ConsultantNode): number {
  let count = root.isActive ? 1 : 0;
  for (const child of root.children) {
    count += countActiveConsultants(child);
  }
  return count;
}

/**
 * Flattens tree into an array of all nodes.
 */
export function flattenTree(root: ConsultantNode): ConsultantNode[] {
  const result: ConsultantNode[] = [root];
  for (const child of root.children) {
    result.push(...flattenTree(child));
  }
  return result;
}

/**
 * Deep clones a consultant node tree.
 */
export function cloneTree(root: ConsultantNode): ConsultantNode {
  return {
    ...root,
    children: root.children.map((child) => cloneTree(child)),
  };
}

/**
 * Recalculates total monthly and cumulative AE bottom-up for the whole tree.
 */
export function recalculateTreeMetrics(root: ConsultantNode): ConsultantNode {
  const updatedChildren = root.children.map((c) => recalculateTreeMetrics(c));

  const childrenMonthlyTurnover = updatedChildren.reduce((sum, c) => sum + c.totalMonthlyAE, 0);
  const childrenCumulativeTurnover = updatedChildren.reduce((sum, c) => sum + c.totalCumulativeAE, 0);

  const totalMonthlyAE = (root.isActive ? root.personalMonthlyAE : 0) + childrenMonthlyTurnover;
  const totalCumulativeAE = root.personalCumulativeAE + childrenCumulativeTurnover;

  return {
    ...root,
    totalMonthlyAE,
    totalCumulativeAE,
    children: updatedChildren,
  };
}

/**
 * Helper to create a new consultant node with default values.
 */
export function createDefaultConsultant(
  id: string,
  name: string,
  level: CareerLevel = 1,
  parentId: string | null = null,
  options?: Partial<ConsultantNode>
): ConsultantNode {
  return {
    id,
    name,
    parentId,
    level,
    personalCumulativeAE: 50,
    personalMonthlyAE: 50,
    totalCumulativeAE: 50,
    totalMonthlyAE: 50,
    monthlyMeetings: 10,
    conversionRate: 0.20,
    recruitmentRate: 1.0,
    isActive: true,
    children: [],
    ...options,
  };
}
